import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Button,
  Input,
  Message,
  useToaster,
  SelectPicker,
  TagPicker,
  Stack,
  List,
  IconButton,
  Loader
} from 'rsuite';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { addAccessProfile } from '@/services/accessProfile.service';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { RiUserLocationLine } from 'react-icons/ri';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

interface AddProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const AddProfileDrawer: React.FC<AddProfileDrawerProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const [formValue, setFormValue] = useState({
    name: '',
    description: ''
  });

  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const floors = useAppSelector((state: any) => state.floor?.data);
  const zonesStore = useAppSelector((state: any) => state.zone?.data);

  const [localAccessList, setLocalAccessList] = useState([
    {
      floor: null,
      zones: []
    }
  ]);

  // Transform floors and zones data
  const floorOptions = floors?.map(floor => ({
    value: floor._id,
    label: floor.name
  })) || [];

  const zoneOptions = zonesStore?.map(zone => ({
    value: zone._id,
    label: zone.name,
    floor: zone.floor
  })) || [];

  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        await Promise.all([dispatch(getFloors()), dispatch(getZones())]);
      } catch (error) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Failed to initialize form data
          </Message>,
          { placement: 'topCenter' }
        );
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      initData();
      setLocalAccessList([{ floor: null, zones: [] }]);
      setSelectedZones([]);
      setFormValue({ name: '', description: '' });
    }
  }, [open, dispatch]);

  const handleAddRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        floor: null,
        zones: []
      }
    ]);
  };

  const handleRemoveRow = (index: number) => {
    const values = [...localAccessList];
    const floorId = values[index]?.floor;
    const floorZones = zoneOptions.filter(zone => zone.floor === floorId);
    const newZones = selectedZones.filter(
      zoneId => !floorZones.some(z => z.value === zoneId)
    );
    
    setSelectedZones(newZones);
    values.splice(index, 1);
    setLocalAccessList(values);
  };

  const handleChangeFloor = (index: number, floorId: string) => {
    const newArr = [...localAccessList];
    newArr[index].floor = floorId;
    newArr[index].zones = [];
    setLocalAccessList(newArr);
  };

  const handleSubmit = async () => {
    try {
      if (!formValue.name || selectedZones.length === 0) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Please fill in all required fields and select at least one zone
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      const submitData = {
        ...formValue,
        zones: selectedZones
      };

      await dispatch(addAccessProfile(submitData));

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Profile created successfully')}
        </Message>,
        { placement: 'topCenter' }
      );

      onClose();
    } catch (error: any) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error creating profile')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  if (loading) return <Loader center content="Loading..." />;
  const { t } = useTranslation();






  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add Access Profile')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Name *')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter profile name" required />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Description')}</Form.ControlLabel>
            <Form.Control
              name="description"
              placeholder="Enter description"
              accepter={Input as any}
            />
          </Form.Group>

          <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
            <RiUserLocationLine /> <h6 className="title">{t('Manage accesses')}</h6>
          </Stack>

          <div className="ml-10">
            <List hover>
              {localAccessList.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <Stack justifyContent="space-around" wrap>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Floor *')}</div>
                      <SelectPicker
                        value={item.floor}
                        onChange={(value) => handleChangeFloor(index, value)}
                        data={floorOptions}
                        placement="auto"
                        style={{ width: 250 }}
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== index)
                          .map(item => item.floor)
                        }
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>Zones *</div>
                      <TagPicker
                        data={zoneOptions.filter(zone => zone.floor === item.floor)}
                        value={selectedZones}
                        onChange={setSelectedZones}
                        style={{ width: 250 }}
                        placement="auto"
                      />
                    </div>
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      icon={<MinusIcon />}
                      size="xs"
                    />
                  </Stack>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default AddProfileDrawer;